// schema.org builders for the blog. Shared by the runtime pages and (in spirit)
// the build time prerender script, which emits the same shapes into static HTML.

import { SITE_NAME, SITE_URL } from '@/components/Seo';

interface ArticleInput {
    slug: string;
    title: string;
    description: string;
    date?: string | null;
    author?: string | null;
    coverImageUrl?: string | null;
}

/** BlogPosting structured data for a single article. */
export function blogPostingJsonLd(post: ArticleInput): Record<string, unknown> {
    const url = `${SITE_URL}/blog/${post.slug}`;
    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        'headline': post.title,
        'description': post.description,
        url,
        'mainEntityOfPage': { '@type': 'WebPage', '@id': url },
        'image': post.coverImageUrl
            ? [/^https?:\/\//i.test(post.coverImageUrl) ? post.coverImageUrl : `${SITE_URL}${post.coverImageUrl}`]
            : [`${SITE_URL}/og-image.png`],
        ...(post.date ? { datePublished: post.date, dateModified: post.date } : {}),
        'author': { '@type': 'Organization', 'name': post.author || SITE_NAME },
        'publisher': {
            '@type': 'Organization',
            'name': SITE_NAME,
            'logo': { '@type': 'ImageObject', 'url': `${SITE_URL}/favicon-512x512.png` },
        },
        'inLanguage': 'en',
    };
}

/** Breadcrumbs so search results show Home > Blog > Article. */
export function blogBreadcrumbJsonLd(title: string, slug: string): Record<string, unknown> {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${SITE_URL}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${SITE_URL}/blog` },
            { '@type': 'ListItem', 'position': 3, 'name': title, 'item': `${SITE_URL}/blog/${slug}` },
        ],
    };
}

/** Blog + ItemList structured data for the index page. */
export function blogListJsonLd(posts: { slug: string; title: string; description: string }[]): Record<string, unknown> {
    return {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        '@id': `${SITE_URL}/blog`,
        'url': `${SITE_URL}/blog`,
        'name': `${SITE_NAME} Blog`,
        'description':
            'Guides and updates on background checks, employee verification, hiring and compliance in Nigeria.',
        'publisher': { '@type': 'Organization', 'name': SITE_NAME, 'url': `${SITE_URL}/` },
        'blogPost': posts.map((p) => ({
            '@type': 'BlogPosting',
            'headline': p.title,
            'description': p.description,
            'url': `${SITE_URL}/blog/${p.slug}`,
        })),
    };
}
